import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { COUNTRIES } from "@/lib/countries";

export const CountrySelector = ({ selectedCountries, onSelectionChange }) => {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (countryCode) => {
    if (selectedCountries.includes(countryCode)) {
      onSelectionChange(selectedCountries.filter((c) => c !== countryCode));
    } else {
      onSelectionChange([...selectedCountries, countryCode]);
    }
  };

  const handleRemove = (countryCode, e) => {
    e.stopPropagation();
    onSelectionChange(selectedCountries.filter((c) => c !== countryCode));
  };

  const getCountryName = (code) => {
    const country = COUNTRIES.find((c) => c.code === code);
    return country ? country.name : code;
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-auto min-h-[44px] bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300"
            data-testid="country-selector-trigger"
          >
            <span className="text-slate-500 font-normal">
              {selectedCountries.length === 0
                ? "Seleccionar países autorizados..."
                : `${selectedCountries.length} país(es) seleccionado(s)`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar país..." className="h-10" />
            <CommandList>
              <CommandEmpty>No se encontró el país.</CommandEmpty>
              <CommandGroup className="max-h-[300px] overflow-y-auto">
                {COUNTRIES.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={country.name}
                    onSelect={() => handleSelect(country.code)}
                    className="cursor-pointer"
                    data-testid={`country-option-${country.code}`}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedCountries.includes(country.code)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    <span className="flex-1">{country.name}</span>
                    <span className="text-xs text-slate-400 font-mono">
                      {country.code}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedCountries.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="selected-countries">
          {selectedCountries.map((code) => (
            <Badge
              key={code}
              variant="secondary"
              className="bg-slate-100 text-slate-700 hover:bg-slate-200 pr-1 gap-1"
            >
              <span>{getCountryName(code)}</span>
              <span className="text-xs text-slate-400">({code})</span>
              <button
                onClick={(e) => handleRemove(code, e)}
                className="ml-1 rounded-full p-0.5 hover:bg-slate-300"
                data-testid={`remove-country-${code}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
